import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import type { SignalView } from '@/lib/api/generated/model';

export interface SignalPayloadDialogProps {
  /** The signal whose payload to show, or `null` when nothing is open. */
  signal: SignalView | null;
  onClose: () => void;
}

/**
 * The body the external system actually sent (spec 090 FR-017, FR-018).
 *
 * **A dialog rather than an inline expander**, and the reasons compound
 * (`research.md` R3). It makes the act unambiguous: an expander can be hit while
 * scanning, and this is content nobody should see by accident. It keeps the rows
 * a uniform height, so the list stays a list. And it keeps third-party content out
 * of the scroll path entirely — a reader hunting for a failure never has a
 * payload pass under their eyes.
 *
 * **Shown as recorded** — `JSON.stringify` with indentation and nothing else. No
 * masking, no redaction, no "safe" rendering (FR-024). Secret handling belongs to
 * the existing transcript-masking work, and a second scheme invented here would
 * be a second thing to keep correct, diverging the first time either changed.
 * This feature's answer to a payload that carries a credential is that nobody
 * sees a payload without asking for it.
 */
export function SignalPayloadDialog({
  signal,
  onClose,
}: SignalPayloadDialogProps) {
  const payload = signal?.payload ?? {};
  const empty = Object.keys(payload).length === 0;

  return (
    <Dialog
      open={signal !== null}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>What arrived</DialogTitle>
          <DialogDescription>
            The body {signal ? `“${signal.external_key}”` : 'this signal'} was
            received with, exactly as recorded.
          </DialogDescription>
        </DialogHeader>

        {empty ? (
          // Said, not drawn as an empty box. `{}` on screen reads as a failure to
          // load the thing rather than as the thing (FR-019).
          <p className="text-muted-foreground text-sm">
            This signal arrived with an empty body.
          </p>
        ) : (
          <pre className="bg-muted max-h-96 overflow-auto rounded-lg p-3 text-xs break-all whitespace-pre-wrap">
            {JSON.stringify(payload, null, 2)}
          </pre>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
